/** The page layout every page follows (2026-10-02): a nav pane, a list
 *  pane and a content pane, as rounded surfaces on a slate ground with an
 *  8 px seam between them.
 *
 *  The nav pane is the app's only left column (the separate app rail is
 *  gone): the apps across its top, the page's header (its main action,
 *  refresh, pin), the page's own nav, and Settings, Appearance, Reload and
 *  the account along its bottom. It is the same on every page: the same
 *  width, drag handle and pin, and on a laptop-width window the same fold
 *  to a strip that opens on hover. Its width and pin are shared by all
 *  pages, so switching pages never moves the panes. */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type FocusEvent,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { RefreshCw } from "lucide-react";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { DegradationBanner } from "./DegradationBanner";
import { TitleBar } from "./TitleBar";
import { StaleCacheBanner } from "./StaleCacheBanner";
import { AppFooter, AppSwitcher } from "./AppNav";
import { publishNavEdge } from "../lib/navEdge";

/** Below this window width the nav pane folds to a strip (laptop screens:
 *  13 to 14 inch MacBooks run 1440 to 1512 points wide). */
export const NAV_COMPACT_QUERY = "(max-width: 1599px)";
export const NAV_STRIP_W = 52;

/** As the app rail: a short grace period so crossing the edge does not close it. */
const NAV_CLOSE_DELAY_MS = 300;
/** Hover intent: the pointer has to rest this long before the pane opens,
 *  so passing over it on the way somewhere else does not (2026-10-02). */
const NAV_OPEN_DELAY_MS = 140;
/** How the pane opens and folds: a soft ease-out over 440 ms. */
const NAV_ANIM = "440ms cubic-bezier(.22,1,.36,1)";

export const NAV_DEFAULT = 288;
export const NAV_MIN = 200;
export const NAV_MAX = 520;
export const LIST_DEFAULT = 440;
/** A content pane on the right that has a set width (the calendar's
 *  event panel). */
export const SIDE_DEFAULT = 380;
export const SIDE_MIN = 320;
export const SIDE_MAX = 760;
const SIDE_W_KEY = "wov.cols.sidePx";

/** That pane's width, dragged by the user and kept per browser. */
export function useSidePaneWidth() {
  const [w, setW] = useState(() => readPx(SIDE_W_KEY, SIDE_DEFAULT, SIDE_MIN, SIDE_MAX));
  useEffect(() => {
    try { localStorage.setItem(SIDE_W_KEY, String(w)); } catch { /* storage unavailable */ }
  }, [w]);
  const set = useCallback((v: number) => setW(clamp(v, SIDE_MIN, SIDE_MAX)), []);
  const reset = useCallback(() => setW(SIDE_DEFAULT), []);
  return { width: w, setWidth: set, reset };
}
export const LIST_MIN = 280;
export const LIST_MAX = 960;

// The keys Mail has always used, so widths and the pin carry over.
const NAV_W_KEY = "mail.cols.sidebarPx";
const LIST_W_KEY = "mail.cols.listPx";
// Unset until the pin is first used: open on a wide window, a strip on a
// laptop-width one. Once set, it holds at every width.
const NAV_PIN_KEY = "wov.nav.pinned";

/** A list or content pane's surface. */
/** A list or content pane's surface (index.css: raised, ringed, shadowed). */
export const PANE_CLASS = "wov-pane";
/** A content pane (the reader): the same surface, a touch apart. */
export const READ_PANE_CLASS = "wov-pane wov-pane--read";

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function readPx(key: string, fallback: number, min: number, max: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? fallback : clamp(n, min, max);
  } catch {
    return fallback;
  }
}

/** The nav and list widths, dragged by the user and kept per browser.
 *  Double-clicking a divider resets it. */
export function useColumnWidths() {
  const [navW, setNavWState] = useState(() => readPx(NAV_W_KEY, NAV_DEFAULT, NAV_MIN, NAV_MAX));
  const [listW, setListWState] = useState(() => readPx(LIST_W_KEY, LIST_DEFAULT, LIST_MIN, LIST_MAX));
  useEffect(() => {
    try { localStorage.setItem(NAV_W_KEY, String(navW)); } catch { /* storage unavailable */ }
  }, [navW]);
  useEffect(() => {
    try { localStorage.setItem(LIST_W_KEY, String(listW)); } catch { /* storage unavailable */ }
  }, [listW]);
  const setNavW = useCallback((v: number) => setNavWState(clamp(v, NAV_MIN, NAV_MAX)), []);
  const setListW = useCallback((v: number) => setListWState(clamp(v, LIST_MIN, LIST_MAX)), []);
  const resetNav = useCallback(() => setNavWState(NAV_DEFAULT), []);
  const resetList = useCallback(() => setListWState(LIST_DEFAULT), []);
  return { navW, listW, setNavW, setListW, resetNav, resetList };
}

/** Opens on hover or keyboard focus and closes shortly after the pointer
 *  leaves. Stays open while the pointer is back over it, or while one of its
 *  native selects (the date ranges) has its menu up. */
export function useHoverExpand(enabled: boolean, ref: RefObject<HTMLElement | null>) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);
  const clear = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);
  useEffect(() => {
    if (!enabled) { clear(); setOpen(false); }
  }, [enabled, clear]);
  useEffect(() => clear, [clear]);
  const openNow = useCallback(() => { clear(); setOpen(true); }, [clear]);
  const openSoon = useCallback(() => {
    clear();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(true);
    }, NAV_OPEN_DELAY_MS);
  }, [clear]);
  const closeSoon = useCallback(() => {
    clear();
    const check = () => {
      timerRef.current = null;
      const el = ref.current;
      if (el?.matches(":hover")) return;
      const active = document.activeElement;
      if (el && active && el.contains(active) && active.tagName === "SELECT") return;
      // A menu opened from the pane (Appearance, the account) sits outside
      // it; stay open until that menu closes, then fold.
      if (el?.querySelector("[data-nav-popover-open]")) {
        timerRef.current = window.setTimeout(check, NAV_CLOSE_DELAY_MS);
        return;
      }
      setOpen(false);
    };
    timerRef.current = window.setTimeout(check, NAV_CLOSE_DELAY_MS);
  }, [clear, ref]);
  const handlers = enabled
    ? {
        onMouseEnter: openSoon,
        onMouseLeave: closeSoon,
        onFocus: openNow,
        onBlur: (e: FocusEvent<HTMLElement>) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) closeSoon();
        },
        // A date range was picked: let go of the select so the pane can close.
        onChange: (e: FormEvent<HTMLElement>) => {
          if (e.target instanceof HTMLSelectElement) e.target.blur();
          closeSoon();
        },
      }
    : {};
  return { open: enabled && open, openNow, handlers };
}

function readPin(): boolean | null {
  try {
    const v = localStorage.getItem(NAV_PIN_KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}

/** The nav pane's state. Pinned, it stays open; unpinned, it rests as a
 *  strip and opens over the list on hover, at any window width, as the app
 *  rail did. Until the pin is used it is pinned on a wide window. */
export function useNavPane() {
  const narrow = useMediaQuery(NAV_COMPACT_QUERY);
  const [choice, setChoice] = useState<boolean | null>(readPin);
  const pinned = choice ?? !narrow;
  const setPinned = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    setChoice((prev) => {
      const value = typeof next === "function" ? next(prev ?? !window.matchMedia(NAV_COMPACT_QUERY).matches) : next;
      try { localStorage.setItem(NAV_PIN_KEY, value ? "1" : "0"); } catch { /* storage unavailable */ }
      return value;
    });
  }, []);
  const compact = !pinned;
  const ref = useRef<HTMLElement | null>(null);
  const hover = useHoverExpand(compact, ref);
  const togglePin = useCallback(() => setPinned((v) => !v), [setPinned]);
  // [ pins or unpins, as it did the app rail.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "[" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setPinned((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPinned]);
  return { narrow, pinned, setPinned, togglePin, compact, ref, hover };
}
export type NavPaneState = ReturnType<typeof useNavPane>;

/** Vertical drag handle between two columns. 6px hit-box wraps a
 *  1px divider; on hover/focus/drag the line thickens to 2px and
 *  picks up a sky tint. Pointer events handle mouse + touch in one
 *  path. Keyboard: arrows nudge by 16px, Home/End jump to bounds.
 *  Double-click resets to the default width. */
/** Within this many pixels of the default width, a drag snaps to it. */
const SNAP_PX = 14;

export function ResizeHandle({
  value, setValue, reset, min, max, label, defaultValue, onMouseEnter, onMouseLeave, invert = false,
}: {
  value: number;
  setValue: (v: number) => void;
  reset: () => void;
  min: number;
  max: number;
  label: string;
  /** The width it snaps to (a stop point) while dragged near it. */
  defaultValue?: number;
  /** The nav pane's hover handlers, so a pane opened by hover stays open
   *  while its edge is held. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** On the left edge of a pane on the right (the calendar's event panel):
   *  dragging left widens it. */
  invert?: boolean;
}) {
  const dir = invert ? -1 : 1;
  const start = useRef<{ x: number; v: number; pid: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const atDefault = defaultValue != null && value === defaultValue;
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, v: value, pid: e.pointerId };
    setDragging(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!start.current || start.current.pid !== e.pointerId) return;
    const next = start.current.v + dir * (e.clientX - start.current.x);
    setValue(defaultValue != null && Math.abs(next - defaultValue) <= SNAP_PX ? defaultValue : next);
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(start.current.pid); } catch { /* already released */ }
    start.current = null;
    setDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft")  { e.preventDefault(); setValue(value - 16 * dir); }
    if (e.key === "ArrowRight") { e.preventDefault(); setValue(value + 16 * dir); }
    if (e.key === "Home")       { e.preventDefault(); setValue(min); }
    if (e.key === "End")        { e.preventDefault(); setValue(max); }
  };
  // The seam between the panes is the divider and the handle fills it; a
  // grip pill sits in its middle. Hover shows a faint line and lights the
  // grip; drag turns both to the accent; keyboard focus shows a focus ring on the
  // 6px hit-box rather than tinting the line, so passive viewing of a
  // recently-focused handle never looks "selected".
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      data-dragging={dragging || undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={() => { start.current = null; setDragging(false); document.body.style.cursor = ""; document.body.style.userSelect = ""; }}
      onDoubleClick={reset}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      title={`${label} · drag, arrows to nudge, double-click to reset`}
      className="hidden md:flex group flex-shrink-0 w-2.5 -mx-2.5 z-10 cursor-col-resize relative items-stretch justify-center touch-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 focus-visible:outline-offset-[-1px] focus:outline-none"
    >
      <span
        aria-hidden
        className={
          "absolute inset-y-0 left-1/2 -translate-x-1/2 transition-[width,background-color] duration-100 " +
          (dragging
            ? "w-[2px] bg-sky-500/70 dark:bg-sky-400/70"
            : "w-[2px] bg-transparent group-hover:bg-slate-400/50 dark:group-hover:bg-slate-500/50")
        }
      />
      {/* The grip: a small pill with three dots, so the seam reads as
          something to drag. Quiet at rest, accent on hover and drag. */}
      <span
        aria-hidden
        className={
          "absolute left-1/2 top-1/2 flex h-9 w-[7px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-[3px] rounded-full shadow-sm transition-all duration-150 " +
          (dragging && atDefault
            ? "scale-y-125 bg-emerald-500 dark:bg-emerald-400"
            : dragging
              ? "scale-y-110 bg-sky-500 dark:bg-sky-400"
              : "bg-slate-300/90 group-hover:scale-y-110 group-hover:bg-sky-500 dark:bg-slate-600/90 dark:group-hover:bg-sky-400")
        }
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={"h-[3px] w-[3px] rounded-full transition-colors " + (dragging ? "bg-white" : "bg-slate-500 group-hover:bg-white dark:bg-slate-300")}
          />
        ))}
      </span>
      {/* The stop point: dragged onto the default width, it says so. */}
      {dragging && atDefault && (
        <span className="wov-fade pointer-events-none absolute left-1/2 top-1/2 z-20 ml-2.5 -translate-y-1/2 whitespace-nowrap rounded-md bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-md">
          Default
        </span>
      )}
    </div>
  );
}

/** The page frame: exactly the viewport, with each pane scrolling on its
 *  own. The document itself does not scroll while a
 *  pane page is mounted (a banner above it used to make the whole page
 *  scroll); restored on unmount. */
export function PaneShell({ children }: { children: ReactNode }) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prev = { html: html.style.overflow, body: body.style.overflow, root: root?.style.overflow ?? "" };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) root.style.overflow = "hidden";
    return () => {
      html.style.overflow = prev.html;
      body.style.overflow = prev.body;
      if (root) root.style.overflow = prev.root;
    };
  }, []);
  return (
    <div className="wov-ground fixed inset-0 flex flex-col overflow-hidden">
      {/* Search, on every page (TitleBar.tsx; the Mac app's title bar). */}
      <TitleBar />
      {/* Shown only when real requests are failing (3+ in 5 minutes). */}
      <div className="px-2.5">
        <DegradationBanner />
        <StaleCacheBanner />
      </div>
      <div className="flex flex-1 min-h-0 gap-2.5 px-2.5 pb-2.5">{children}</div>
    </div>
  );
}

/** The nav pane: its slot keeps the list's place; the pane inside is as
 *  wide as the slot, or, compact and hovered, wider and over the list.
 *  `strip` is what shows while it is folded. The resize handle follows it
 *  whenever it is open, pinned or opened by hover (2026-10-02: unpinned it
 *  had none, so its width could not be changed). */
export function NavPane({
  pane,
  width,
  setWidth,
  resetWidth,
  header,
  strip,
  label,
  children,
}: {
  pane: NavPaneState;
  width: number;
  setWidth: (v: number) => void;
  resetWidth: () => void;
  header: ReactNode;
  strip: ReactNode;
  /** For the resize handle ("Resize folder sidebar"). */
  label: string;
  children: ReactNode;
}) {
  const folded = pane.compact && !pane.hover.open;
  // Unpinned, the pane opens on hover by widening its own slot, so the list
  // and reader move aside and stay visible and usable instead of being
  // covered. It used to animate the slot's width, which re-laid out the
  // list and the message every frame and stuttered; now (2026-10-02) the
  // layout changes once and the move is played with transforms and a clip
  // (FLIP), which the compositor runs smoothly.
  const slotRef = useRef<HTMLDivElement | null>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  const shownW = folded ? NAV_STRIP_W : width;
  const prevW = useRef(shownW);
  const prevFolded = useRef(folded);
  const cleanup = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    const oldW = prevW.current;
    prevW.current = shownW;
    const wasFolded = prevFolded.current;
    prevFolded.current = folded;
    const slot = slotRef.current;
    const aside = asideRef.current;
    // Only opening and folding are played; a drag of the edge follows the
    // pointer as it is.
    if (!slot || !aside || oldW === shownW || !pane.compact || wasFolded === folded) return;
    cleanup.current?.();
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const delta = shownW - oldW; // > 0 opening, < 0 folding
    // The panes and their handles move; never an open dialog in the row.
    const siblings = (Array.from(slot.parentElement?.children ?? []) as HTMLElement[]).filter(
      (el) => el !== slot && el.matches("section, .wov-pane, [role='separator']"),
    );
    const wide = Math.max(oldW, shownW);
    // Invert: everything starts where it was.
    slot.style.overflow = "visible";
    aside.style.right = "auto";
    aside.style.width = `${wide}px`;
    aside.style.transition = "none";
    aside.style.clipPath = `inset(0 ${wide - oldW}px 0 0 round 12px)`;
    for (const el of siblings) {
      el.style.transition = "none";
      el.style.transform = `translateX(${-delta}px)`;
    }
    void slot.offsetWidth;
    // Play: to where the new layout puts them.
    aside.style.transition = `clip-path ${NAV_ANIM}`;
    aside.style.clipPath = `inset(0 ${wide - shownW}px 0 0 round 12px)`;
    for (const el of siblings) {
      el.style.transition = `transform ${NAV_ANIM}`;
      el.style.transform = "translateX(0)";
    }
    const done = () => {
      window.clearTimeout(timer);
      slot.style.overflow = "";
      aside.style.right = "";
      aside.style.width = "";
      aside.style.transition = "";
      aside.style.clipPath = "";
      for (const el of siblings) {
        el.style.transition = "";
        el.style.transform = "";
      }
      cleanup.current = null;
    };
    const timer = window.setTimeout(done, 480);
    cleanup.current = done;
  }, [shownW, folded, pane.compact]);
  useEffect(() => () => cleanup.current?.(), []);
  // The resting edge for the title bar (see useNavEdge).
  const restingW = pane.compact ? NAV_STRIP_W : width;
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const publish = () => publishNavEdge(Math.round(slot.getBoundingClientRect().left + restingW));
    publish();
    window.addEventListener("resize", publish);
    return () => window.removeEventListener("resize", publish);
  }, [restingW]);
  // A layout cleanup: on a page change it runs before the next page's pane
  // publishes, so it never clears the new edge.
  useLayoutEffect(() => () => publishNavEdge(null), []);
  const setAsideRef = useCallback((el: HTMLElement | null) => {
    asideRef.current = el;
    (pane.ref as { current: HTMLElement | null }).current = el;
  }, [pane.ref]);
  return (
    <>
      <div
        ref={slotRef}
        className="relative z-10 flex-shrink-0 overflow-hidden rounded-xl"
        style={{ width: shownW }}
      >
        <aside
          ref={setAsideRef}
          {...pane.hover.handlers}
          className="wov-pane wov-pane--nav absolute inset-0 flex flex-col min-h-0 overflow-hidden"
        >
          {/* Laid out at the full width even while the pane is a strip, so
              opening it reveals the panels instead of reflowing them; it
              fades in as the slot widens. */}
          <div
            className={
              "flex min-h-0 flex-1 flex-col transition-opacity duration-300 ease-out motion-reduce:transition-none " +
              (folded ? "pointer-events-none opacity-0" : "opacity-100 delay-75")
            }
            style={{ width }}
          >
            <AppSwitcher orientation="row" />
            {header}
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
            <AppFooter orientation="row" pin={{ pinned: pane.pinned, onToggle: pane.togglePin, what: "pane" }} />
          </div>
          {folded && <div className="wov-fade">{strip}</div>}
        </aside>
      </div>
      {!folded && (
        <ResizeHandle
          value={width}
          setValue={setWidth}
          reset={resetWidth}
          min={NAV_MIN}
          max={NAV_MAX}
          label={label}
          defaultValue={NAV_DEFAULT}
          onMouseEnter={pane.compact ? pane.hover.openNow : undefined}
          onMouseLeave={pane.compact ? (pane.hover.handlers as { onMouseLeave?: () => void }).onMouseLeave : undefined}
        />
      )}
    </>
  );
}

const HEADER_ICON_BTN = "rounded-lg p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-700/40";

/** The nav pane's header: the page's main action (or its name), then
 *  refresh. Settings and the pin are at the bottom of the pane, with the
 *  rest of the app's controls. */
export function NavPaneHeader({
  primary,
  title,
  onRefresh,
  loading = false,
  pane,
  what,
}: {
  primary?: { label: string; icon: ComponentType<{ className?: string }>; onClick: () => void; title?: string };
  /** Shown instead of a main action ("Settings"). */
  title?: string;
  onRefresh?: () => void;
  loading?: boolean;
  pane: NavPaneState;
  /** What the pin keeps open, for its tooltip ("folders", "views"). */
  what: string;
}) {
  const Icon = primary?.icon;
  return (
    <div className="mt-1.5 px-2.5 py-1.5 border-b border-slate-200/70 dark:border-slate-700/40 flex items-center justify-between gap-2">
      {primary && Icon ? (
        <button
          type="button"
          onClick={primary.onClick}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
          title={primary.title ?? primary.label}
        >
          <Icon className="w-4 h-4" />
          {primary.label}
        </button>
      ) : (
        <span className="px-2.5 py-1.5 text-[13px] font-semibold text-slate-800 dark:text-slate-100">{title}</span>
      )}
      <div className="flex items-center gap-1">
        {onRefresh && (
          <button type="button" onClick={onRefresh} className={HEADER_ICON_BTN} title="Refresh">
            <RefreshCw className={`w-4 h-4 text-sky-500 dark:text-sky-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}

      </div>
    </div>
  );
}

export type StripItem = { id: string; icon: ReactNode; title: string; active: boolean; onClick: () => void };

/** The nav pane folded: the apps, the page's main action and one button
 *  per section or view (the current one marked), then Settings,
 *  Appearance, Reload, the account and the pin. Hovering anywhere opens
 *  the pane; an item button also picks that item. */
export function NavPaneStrip({
  primary,
  items,
  pane,
  what,
}: {
  primary?: { title: string; icon: ComponentType<{ className?: string }>; onClick: () => void };
  items: StripItem[];
  pane: NavPaneState;
  what: string;
}) {
  const Icon = primary?.icon;
  return (
    <div className="absolute inset-y-0 left-0 flex flex-col items-center gap-1 py-2" style={{ width: NAV_STRIP_W }}>
      <AppSwitcher orientation="column" />
      <div className="my-1 h-px w-6 shrink-0 bg-slate-200 dark:bg-slate-700/60" />
      <div className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto">
        {primary && Icon && (
          <button
            type="button"
            onClick={primary.onClick}
            // The page's main action (New mail, New meeting, New task): a
            // filled tile, so it does not read as one more view.
            className="mb-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-600 text-white shadow-sm transition hover:bg-sky-500 active:scale-95"
            title={primary.title}
            aria-label={primary.title}
          >
            <Icon className="h-[18px] w-[18px]" />
          </button>
        )}
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => { it.onClick(); pane.hover.openNow(); }}
            title={it.title}
            aria-label={it.title}
            className={
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base leading-none transition-colors " +
              (it.active ? "bg-white dark:bg-slate-800 ring-1 ring-slate-300 dark:ring-slate-600 shadow-sm" : "hover:bg-slate-200/60 dark:hover:bg-slate-700/40")
            }
          >
            {it.icon}
          </button>
        ))}
      </div>
      <div className="my-1 h-px w-6 shrink-0 bg-slate-200 dark:bg-slate-700/60" />
      <AppFooter orientation="column" pin={{ pinned: pane.pinned, onToggle: pane.togglePin, what }} />
    </div>
  );
}

/** A small uppercase heading over a group of nav rows. */
export function NavGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{title}</div>
      {children}
    </div>
  );
}

/** What an empty content pane says ("Select a message to read."). */
export function PaneEmpty({ children }: { children: ReactNode }) {
  return <div className="flex-1 flex items-center justify-center text-sm text-slate-400 italic">{children}</div>;
}
